import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Ticket, CheckCircle, CheckCircle2, AlertTriangle,
  Users, ArrowRight, UserCheck, Activity,
  ArrowUpRight, Loader2, UserPlus, GitMerge,
  Edit3, Plus, Circle,
  RefreshCw, Rocket, Settings, PenLine, FolderOpen, RotateCcw,
  CalendarDays, FolderKanban,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { sd } from '@sd/lib/sdPaths';
import { useQuery } from '@tanstack/react-query';
import { ServiceDeskAPI, ptsOpenProjectListQueryKey } from '@sd/services/APIs';
import { formatStatusLabel } from '@sd/lib/ticketStreamUtils';
import {
  projectFilterOptionsWithSelection,
  ptsDropdownOptionsFromPayload,
} from '@sd/lib/ptsDropdownOptions';
import { normalizeMultiFilterValues } from '@sd/lib/ticketFilterApi';
import { SearchableMultiFilterSelect } from '@sd/components/common/SearchableMultiFilterSelect';
import {
  buildAdminTicketViewPath,
  ticketIdFromDashboardRow,
} from '@sd/lib/adminTicketDeepLink';
import { buildAdminAgentInProgressTicketsPath, buildAdminRecentTicketsListPath } from '@sd/lib/adminTicketUtils';

/** Today's date as `YYYY-MM-DD` (native date-input value). */
function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** `YYYY-MM-DD` → `DD/MM/YYYY` (API `date` param + display). */
function isoToDisplay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso ?? '').trim());
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

const isValidIsoDate = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);

/**
 * Dashboard filters persist across refresh (incl. Ctrl+Shift+R) via sessionStorage
 * — same idea as the ticket toolbar, minus Recoil (single component, no sharing).
 */
const DASHBOARD_FILTERS_STORAGE_KEY = 'sd:admin-dashboard-filters:v1';

interface SavedDashboardFilters {
  projectFilters: string[];
  dateFilter: string;
}

/** Read persisted filters (null = nothing saved → apply first-load defaults). */
function readSavedDashboardFilters(): SavedDashboardFilters | null {
  try {
    const raw = sessionStorage.getItem(DASHBOARD_FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return null;
    const projectFilters = Array.isArray(parsed.projectFilters)
      ? parsed.projectFilters.filter((v): v is string => typeof v === 'string')
      : [];
    const dateFilter =
      typeof parsed.dateFilter === 'string' ? parsed.dateFilter : '';
    return { projectFilters, dateFilter };
  } catch {
    return null;
  }
}

function persistDashboardFilters(state: SavedDashboardFilters): void {
  try {
    sessionStorage.setItem(DASHBOARD_FILTERS_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage disabled / over quota — non-fatal */
  }
}

/** Coerce API value → integer, trying each key in order. */
function pickNumber(obj: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '') {
      const p = parseInt(v, 10);
      if (!Number.isNaN(p)) return p;
    }
  }
  return 0;
}

/** Extract `status_activity.today_status` + `.general` counters (with key fallbacks). */
function normalizeStatusActivity(raw: unknown) {
  const root = (raw ?? {}) as Record<string, unknown>;
  const sa = (root.status_activity ?? {}) as Record<string, unknown>;
  const today = (sa.today_status ?? {}) as Record<string, unknown>;
  const general = (sa.general ?? {}) as Record<string, unknown>;
  return {
    today: {
      createdToday:      pickNumber(today, 'created_in_progress', 'created_today', 'createdInProgress'),
      closed:            pickNumber(today, 'closed', 'closed_tickets'),
      statusUpdated:     pickNumber(today, 'status_updated', 'statusUpdated'),
      movedToProduction: pickNumber(today, 'moved_to_production', 'movedToProduction'),
    },
    general: {
      openInProgress: pickNumber(general, 'open_in_progress', 'openInProgress', 'open'),
      readyForQa:     pickNumber(general, 'ready_for_qa', 'readyForQa'),
      projects:       pickNumber(general, 'projects'),
      reopen:         pickNumber(general, 'reopen'),
    },
  };
}

type StatTone = 'blue' | 'green' | 'amber' | 'purple' | 'teal' | 'orange';

/** Color used only as a thin accent bar + icon chip; card body stays on theme (`bg-card`). */
const toneStyles: Record<StatTone, { bar: string; iconBg: string; icon: string }> = {
  blue:   { bar: 'bg-blue-500',    iconBg: 'bg-blue-50 dark:bg-blue-950/40',       icon: 'text-blue-600 dark:text-blue-400' },
  green:  { bar: 'bg-emerald-500', iconBg: 'bg-emerald-50 dark:bg-emerald-950/40', icon: 'text-emerald-600 dark:text-emerald-400' },
  amber:  { bar: 'bg-amber-500',   iconBg: 'bg-amber-50 dark:bg-amber-950/40',     icon: 'text-amber-600 dark:text-amber-400' },
  purple: { bar: 'bg-purple-500',  iconBg: 'bg-purple-50 dark:bg-purple-950/40',   icon: 'text-purple-600 dark:text-purple-400' },
  teal:   { bar: 'bg-teal-500',    iconBg: 'bg-teal-50 dark:bg-teal-950/40',       icon: 'text-teal-600 dark:text-teal-400' },
  orange: { bar: 'bg-orange-500',  iconBg: 'bg-orange-50 dark:bg-orange-950/40',   icon: 'text-orange-600 dark:text-orange-400' },
};

function StatCard({
  icon: Icon,
  label,
  value,
  tone,
  loading,
}: {
  icon: LucideIcon;
  label: string;
  value: number;
  tone: StatTone;
  loading: boolean;
}) {
  const s = toneStyles[tone];
  return (
    <Card className="flex flex-col overflow-hidden border-border bg-card shadow-sm transition-shadow hover:shadow-md">
      <div className={cn('h-1 w-full shrink-0', s.bar)} />
      <CardContent className="flex items-start gap-3 p-3 sm:p-4">
        <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', s.iconBg, s.icon)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="break-words text-xs font-medium leading-snug text-muted-foreground" title={label}>
            {label}
          </p>
          {loading
            ? <Loader2 className="mt-1 h-5 w-5 animate-spin text-muted-foreground" />
            : <p className="mt-0.5 text-2xl font-bold tabular-nums text-foreground">{value}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

/** Single-date picker: styled trigger over a hidden native date input. `max` caps selectable dates. */
function SingleDatePicker({
  value,
  onChange,
  max,
}: {
  value: string;
  onChange: (next: string) => void;
  max?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const openPicker = () => {
    const el = ref.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') el.showPicker();
    else { el.focus(); el.click(); }
  };
  return (
    <div className="relative w-full">
      <button
        type="button"
        onClick={openPicker}
        aria-label="Dashboard date"
        className="flex h-9 w-full items-center rounded-md border border-input bg-background px-3 pr-9 text-left text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        <span className="min-w-0 flex-1 truncate">{isoToDisplay(value) || 'Select date'}</span>
      </button>
      <CalendarDays className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground" />
      <input
        ref={ref}
        type="date"
        value={value}
        max={max}
        onChange={e => onChange(e.target.value)}
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
      />
    </div>
  );
}

function extractApiArray(raw: unknown, ...keys: string[]): Record<string, unknown>[] {
  const d = (raw ?? {}) as Record<string, unknown>;
  for (const k of keys) {
    const v = d[k];
    if (Array.isArray(v)) return v as Record<string, unknown>[];
  }
  return [];
}

/** Newest tickets first — matches main-dashboard `created_on` order. */
function sortDashboardTicketsByCreatedDesc(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return [...rows].sort((a, b) => {
    const at = Date.parse(String(a.created_on ?? a.created_at ?? a.createdAt ?? ""));
    const bt = Date.parse(String(b.created_on ?? b.created_at ?? b.createdAt ?? ""));
    return (Number.isNaN(bt) ? 0 : bt) - (Number.isNaN(at) ? 0 : at);
  });
}

/** Sort agents by in-progress ticket count (highest first). */
function sortAgentsByOpenDesc(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return [...rows].sort((a, b) => {
    const inProgressA =
      Number(a.all_in_progress_tickets_count ?? a.all_pending_open_tickets_count ?? a.open_tickets ?? a.pending_tickets ?? 0) || 0;
    const inProgressB =
      Number(b.all_in_progress_tickets_count ?? b.all_pending_open_tickets_count ?? b.open_tickets ?? b.pending_tickets ?? 0) || 0;
    return inProgressB - inProgressA;
  });
}

function agentTicketCounts(agent: Record<string, unknown>): { inProgress: number; assigned: number } {
  return {
    inProgress:
      Number(agent.all_in_progress_tickets_count ?? agent.all_pending_open_tickets_count ?? agent.open_tickets ?? agent.pending_tickets ?? 0) || 0,
    assigned: Number(agent.all_assigned_tickets_count ?? agent.assigned_tickets ?? 0) || 0,
  };
}

function fmtCell(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') { try { return JSON.stringify(v); } catch { return String(v); } }
  return String(v);
}

const isUuid = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(v);

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const statusColors: Record<string, string> = {
  open:     'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  closed:   'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
};

const priorityColors: Record<string, string> = {
  'P1': 'bg-red-500 text-white',
  'P1 - Critical': 'bg-red-500 text-white',
  'P2': 'bg-orange-400 text-white',
  'P2 - High': 'bg-orange-400 text-white',
  'P3': 'bg-blue-400 text-white',
  'P3 - Medium': 'bg-blue-400 text-white',
  'P4': 'bg-gray-400 text-white',
};

function priorityShort(p: string): string {
  if (!p || p === '—') return '';
  if (p.startsWith('P1')) return 'P1';
  if (p.startsWith('P2')) return 'P2';
  if (p.startsWith('P3')) return 'P3';
  if (p.startsWith('P4')) return 'P4';
  return p;
}

/** Icon + color for activity_type */
function activityIcon(type: string) {
  const t = String(type ?? '').toLowerCase();
  if (t === 'create') return { icon: Plus,        color: 'text-green-500',  bg: 'bg-green-50 dark:bg-green-950/40' };
  if (t === 'edit')   return { icon: Edit3,        color: 'text-blue-500',   bg: 'bg-blue-50 dark:bg-blue-950/40' };
  if (t.includes('escalat'))  return { icon: ArrowUpRight, color: 'text-orange-500', bg: 'bg-orange-50 dark:bg-orange-950/40' };
  if (t.includes('assign'))   return { icon: UserPlus,     color: 'text-purple-500', bg: 'bg-purple-50 dark:bg-purple-950/40' };
  if (t.includes('merge'))    return { icon: GitMerge,     color: 'text-indigo-500', bg: 'bg-indigo-50 dark:bg-indigo-950/40' };
  if (t.includes('sla'))      return { icon: AlertTriangle,color: 'text-yellow-500', bg: 'bg-yellow-50 dark:bg-yellow-950/40' };
  if (t.includes('resolve'))  return { icon: CheckCircle,  color: 'text-green-500',  bg: 'bg-green-50 dark:bg-green-950/40' };
  if (t.includes('close'))    return { icon: Circle,       color: 'text-gray-500',   bg: 'bg-gray-50 dark:bg-gray-950/40' };
  return { icon: Activity, color: 'text-muted-foreground', bg: 'bg-muted' };
}

export function AdminDashboard() {
  const navigate = useNavigate();

  const openDashboardTicket = (ticket: Record<string, unknown>) => {
    const ticketId = ticketIdFromDashboardRow(ticket);
    if (!ticketId) return;
    navigate(buildAdminTicketViewPath(ticketId, { returnTo: sd("admin") }));
  };

  // Restore persisted selection synchronously (survives refresh / Ctrl+Shift+R).
  const savedFilters = useRef(readSavedDashboardFilters()).current;
  const hasSavedFilters = savedFilters !== null;

  // Filters — restored value, else default (today + NIB CAD/RMS applied on list load).
  const [projectFilters, setProjectFilters] = useState<string[]>(
    () => savedFilters?.projectFilters ?? []
  );
  const [dateFilter, setDateFilter] = useState<string>(() => {
    const d = savedFilters?.dateFilter?.trim();
    return d && isValidIsoDate(d) && d <= todayIso() ? d : todayIso();
  });
  const [openFilterId, setOpenFilterId] = useState<string | null>(null);
  // When a selection was restored, defaults are already resolved — skip NIB auto-pick.
  const [defaultsReady, setDefaultsReady] = useState(hasSavedFilters);
  // Persist ONLY user-initiated changes — the auto NIB default is never saved.
  const userTouchedRef = useRef(hasSavedFilters);

  const handleProjectFiltersChange = useCallback((v: string[]) => {
    userTouchedRef.current = true;
    setProjectFilters(v);
  }, []);
  const handleDateChange = useCallback((v: string) => {
    userTouchedRef.current = true;
    setDateFilter(v);
  }, []);

  const {
    data: ptsProjectsData,
    isPending: projectsLoading,
    isError: projectsError,
  } = useQuery({
    queryKey: ptsOpenProjectListQueryKey,
    queryFn: () => ServiceDeskAPI.pts.openProjectList().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  // One-time default selection once the list resolves: prefer NIB CAD + NIB RMS
  // (matched by name), else fall back to the first 2 so it's never empty.
  // On error, proceed with no project filter so the dashboard still loads.
  useEffect(() => {
    if (defaultsReady) return;
    if (ptsProjectsData === undefined) {
      if (projectsError) setDefaultsReady(true);
      return;
    }
    const opts = ptsDropdownOptionsFromPayload(ptsProjectsData);
    if (opts.length > 0) {
      const wanted = new Set(['nib cad', 'nib rms']);
      const matched = opts.filter(o => wanted.has(o.label.trim().toLowerCase()));
      const chosen = matched.length > 0 ? matched : opts.slice(0, 2);
      setProjectFilters(chosen.map(o => o.value));
    }
    setDefaultsReady(true);
  }, [ptsProjectsData, projectsError, defaultsReady]);

  // Persist ONLY after a user change (never the auto NIB default), once defaults resolved.
  useEffect(() => {
    if (!defaultsReady || !userTouchedRef.current) return;
    persistDashboardFilters({ projectFilters, dateFilter });
  }, [defaultsReady, projectFilters, dateFilter]);

  const projectOptions = useMemo(
    () => projectFilterOptionsWithSelection(ptsProjectsData, projectFilters),
    [ptsProjectsData, projectFilters]
  );

  const projectParam = useMemo(
    () => normalizeMultiFilterValues(projectFilters).join(','),
    [projectFilters]
  );

  const { data: rawDashboard, isPending: dashPending, isError: dashError } = useQuery({
    queryKey: ['sd-main-dashboard', dateFilter, projectParam || 'all'],
    queryFn: () =>
      ServiceDeskAPI.dashboard
        .main({ date: isoToDisplay(dateFilter), project: projectParam })
        .then(r => r.data),
    enabled: defaultsReady,
    staleTime: 60_000,
    retry: 1,
  });

  // Pre-default window counts as loading so cards show spinners, not stale zeros.
  const dashLoading = !defaultsReady || dashPending;

  const activity = normalizeStatusActivity(rawDashboard);

  const recentTickets     = extractApiArray(rawDashboard, 'recent_tickets',   'recentTickets',  'tickets').slice(0, 5);
  const recentActivity    = extractApiArray(rawDashboard, 'live_activity',    'recent_activity','recentActivity', 'activity_log').slice(0, 8);
  const unassignedTickets = useMemo(
    () =>
      sortDashboardTicketsByCreatedDesc(
        extractApiArray(rawDashboard, 'unassigned', 'unassigned_tickets', 'unassignedTickets')
      ),
    [rawDashboard]
  );
  const unassignedDisplayed = useMemo(
    () => unassignedTickets.slice(0, 4),
    [unassignedTickets]
  );
  const agentsList = useMemo(
    () => sortAgentsByOpenDesc(extractApiArray(rawDashboard, 'agent_status', 'agents', 'agent_list')),
    [rawDashboard]
  );
  const agentsDisplayed = useMemo(() => agentsList.slice(0, 6), [agentsList]);

  return (
    <div className="min-w-0 space-y-4 p-4 sm:space-y-6 sm:p-6">
      {/* Header */}
      <div className="min-w-0">
        <h1 className="text-xl font-bold text-foreground sm:text-2xl">Admin Dashboard</h1>
        <p className="text-sm text-muted-foreground">Real-time overview of helpdesk operations</p>
      </div>

      {dashError && (
        <p className="text-sm text-destructive">Failed to load dashboard data.</p>
      )}

      {/* Filters: Project (multi) + Date */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="w-full min-w-0 sm:w-[280px]">
          <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <FolderKanban className="h-3.5 w-3.5" />
            Project
          </label>
          <SearchableMultiFilterSelect
            selectId="dashboard-project"
            openSelectId={openFilterId}
            onOpenSelectIdChange={setOpenFilterId}
            values={projectFilters}
            onValuesChange={handleProjectFiltersChange}
            options={projectOptions}
            placeholder="All Projects"
            searchPlaceholder="Search projects…"
            disabled={projectsLoading}
            triggerClassName="h-9 w-full"
          />
        </div>
        <div className="w-full min-w-0 sm:w-[160px]">
          <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <CalendarDays className="h-3.5 w-3.5" />
            Date
          </label>
          <SingleDatePicker value={dateFilter} onChange={handleDateChange} max={todayIso()} />
        </div>
      </div>

      {/* Today status */}
      <div className="space-y-2">
        <p className="text-sm font-semibold text-foreground">Today status</p>
        <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
          <StatCard icon={Ticket}       label="Tickets Created Today" value={activity.today.createdToday}      tone="blue"   loading={dashLoading} />
          <StatCard icon={CheckCircle2} label="Closed Tickets"        value={activity.today.closed}            tone="green"  loading={dashLoading} />
          <StatCard icon={RefreshCw}    label="Status Updated"        value={activity.today.statusUpdated}     tone="amber"  loading={dashLoading} />
          <StatCard icon={Rocket}       label="Moved to Production"   value={activity.today.movedToProduction} tone="purple" loading={dashLoading} />
        </div>
      </div>

      {/* General cards */}
      <div className="space-y-2">
        <p className="text-sm font-semibold text-foreground">General cards</p>
        <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
          <StatCard icon={Settings}   label="Total In Progress (Open Tickets)" value={activity.general.openInProgress} tone="blue"   loading={dashLoading} />
          <StatCard icon={PenLine}    label="Ready for QA"                     value={activity.general.readyForQa}     tone="teal"   loading={dashLoading} />
          <StatCard icon={FolderOpen} label="Projects"                         value={activity.general.projects}       tone="purple" loading={dashLoading} />
          <StatCard icon={RotateCcw}  label="Reopen"                           value={activity.general.reopen}         tone="orange" loading={dashLoading} />
        </div>
      </div>

      {/* 3-column layout */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">

        {/* Col 1: Recent Tickets */}
        <Card className="lg:col-span-1">
          <CardHeader className="flex flex-col gap-2 pb-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-base">Recent Tickets</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(buildAdminRecentTicketsListPath())} className="h-8 w-full gap-1 text-xs sm:w-auto">
              View All <ArrowRight className="h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {dashLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : recentTickets.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-8">No recent tickets</p>
            ) : recentTickets.map((ticket, i) => {
              const uiId     = fmtCell(ticket['ui_id'] ?? ticket['uiId']);
              const subject  = fmtCell(ticket['subject'] ?? ticket['title']);
              const status   = String(ticket['status'] ?? '').toLowerCase();
              const priority = fmtCell(ticket['priority']);
              const pShort   = priorityShort(priority);
              const rawReq   = fmtCell(ticket['requester_name'] ?? ticket['requester']);
              const requester = rawReq !== '—' && !isUuid(rawReq) ? rawReq : null;
              const channel  = fmtCell(ticket['channel']);
              const ticketId = ticketIdFromDashboardRow(ticket);
              return (
                <div
                  key={String(ticket['id'] ?? i)}
                  role={ticketId ? "button" : undefined}
                  tabIndex={ticketId ? 0 : undefined}
                  title={ticketId ? `View ${uiId !== '—' ? uiId : 'ticket'}` : undefined}
                  onClick={ticketId ? () => openDashboardTicket(ticket) : undefined}
                  onKeyDown={
                    ticketId
                      ? e => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            openDashboardTicket(ticket);
                          }
                        }
                      : undefined
                  }
                  className={cn(
                    "flex items-start justify-between gap-2 rounded-lg border p-2.5 transition-colors hover:bg-muted/50",
                    ticketId && "cursor-pointer hover:border-primary/30"
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
                      {uiId !== '—' && (
                        <span className="shrink-0 whitespace-nowrap font-mono text-xs font-semibold text-primary">
                          {uiId}
                        </span>
                      )}
                      {pShort && (
                        <Badge className={cn("text-[10px] px-1.5 py-0 rounded-full", priorityColors[priority] ?? priorityColors[pShort] ?? 'bg-muted text-muted-foreground')}>
                          {pShort}
                        </Badge>
                      )}
                    </div>
                    <p
                      className="whitespace-normal break-words text-sm font-medium leading-snug"
                      title={subject !== '—' ? subject : undefined}
                    >
                      {subject}
                    </p>
                    {(requester || channel !== '—') && (
                      <p className="break-words text-xs text-muted-foreground">
                        {requester}{requester && channel !== '—' ? ' • ' : ''}{channel !== '—' ? channel : ''}
                      </p>
                    )}
                  </div>
                  {status && (
                    <Badge className={cn("text-[10px] ml-2 flex-shrink-0 self-start", statusColors[status] ?? 'bg-muted text-muted-foreground')}>
                      {formatStatusLabel(status)}
                    </Badge>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Col 2: Live Activity */}
        <Card className="lg:col-span-1">
          <CardHeader className="flex flex-col gap-2 pb-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-base">Live Activity</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate(sd('admin/activity'))} className="h-8 w-full gap-1 text-xs sm:w-auto">
              View All <ArrowRight className="h-3 w-3" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-1">
            {dashLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            ) : recentActivity.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-8">No recent activity</p>
            ) : recentActivity.map((entry, i) => {
              const title   = fmtCell(entry['activity_title']  ?? entry['action'] ?? entry['event']);
              const actType = String(entry['activity_type']    ?? entry['type']   ?? '');
              const at      = String(entry['created_on']       ?? entry['performed_at'] ?? entry['timestamp'] ?? '');
              const { icon: AIcon, color: aColor, bg: aBg } = activityIcon(
                title.toLowerCase().includes('escalat') ? 'escalat'
                : title.toLowerCase().includes('assign') ? 'assign'
                : title.toLowerCase().includes('sla')   ? 'sla'
                : title.toLowerCase().includes('resolv')? 'resolve'
                : title.toLowerCase().includes('close') ? 'close'
                : actType
              );
              return (
                <div key={String(entry['id'] ?? i)} className="flex items-start gap-2.5 p-2 rounded-md hover:bg-muted/30 transition-colors">
                  <div className={cn("mt-0.5 h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0", aBg)}>
                    <AIcon className={cn("h-3 w-3", aColor)} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className="whitespace-normal break-words text-xs font-medium leading-snug"
                      title={title !== '—' ? title : undefined}
                    >
                      {title}
                    </p>
                    {at && <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(at)}</p>}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Col 3: Unassigned + Agent Status */}
        <div className="space-y-4">

          {/* Unassigned */}
          <Card className="border-yellow-500/30">
            <CardHeader className="flex flex-col gap-2 pb-2 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-yellow-500" />
                Unassigned ({dashLoading ? '…' : unassignedDisplayed.length})
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate(sd('admin/tickets?unassigned=true'))} className="h-8 w-full gap-1 text-xs sm:w-auto">
                View All <ArrowRight className="h-3 w-3" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {dashLoading ? (
                <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
              ) : unassignedDisplayed.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">No unassigned tickets ✓</p>
              ) : unassignedDisplayed.map((ticket, i) => {
                const uiId     = fmtCell(ticket['ui_id'] ?? ticket['uiId']);
                const subject  = fmtCell(ticket['subject'] ?? ticket['title']);
                const status   = String(ticket['status'] ?? '').toLowerCase();
                const priority = fmtCell(ticket['priority']);
                const pShort   = priorityShort(priority);
                const category = fmtCell(ticket['category']);
                const ticketType = fmtCell(ticket['tickettype'] ?? ticket['ticket_type']);
                const ticketId = ticketIdFromDashboardRow(ticket);
                return (
                  <div
                    key={String(ticket['id'] ?? i)}
                    role={ticketId ? "button" : undefined}
                    tabIndex={ticketId ? 0 : undefined}
                    title={ticketId ? `View ${uiId !== '—' ? uiId : 'ticket'}` : undefined}
                    className={cn(
                      "cursor-pointer rounded-lg border p-2.5 transition-colors hover:bg-muted/50",
                      ticketId && "hover:border-primary/30"
                    )}
                    onClick={ticketId ? () => openDashboardTicket(ticket) : undefined}
                    onKeyDown={
                      ticketId
                        ? e => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openDashboardTicket(ticket);
                            }
                          }
                        : undefined
                    }
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
                          {uiId !== '—' && (
                            <span className="shrink-0 whitespace-nowrap font-mono text-xs font-semibold text-primary">
                              {uiId}
                            </span>
                          )}
                          {pShort && (
                            <Badge className={cn("text-[10px] px-1.5 py-0 rounded-full", priorityColors[priority] ?? priorityColors[pShort] ?? 'bg-muted text-muted-foreground')}>
                              {pShort}
                            </Badge>
                          )}
                        </div>
                        <p
                          className="whitespace-normal break-words text-sm font-medium leading-snug"
                          title={subject !== '—' ? subject : undefined}
                        >
                          {subject}
                        </p>
                        {(category !== '—' || ticketType !== '—') && (
                          <p className="break-words text-xs text-muted-foreground capitalize">
                            {[category !== '—' ? category : null, ticketType !== '—' ? ticketType : null]
                              .filter(Boolean)
                              .map(v => (v as string).replace(/[_-]+/g, ' '))
                              .join(' • ')}
                          </p>
                        )}
                      </div>
                      {status && (
                        <Badge className={cn("ml-2 flex-shrink-0 text-[10px]", statusColors[status] ?? 'bg-muted text-muted-foreground')}>
                          {formatStatusLabel(status)}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Agents — Most Open */}
          <Card>
            <CardHeader className="flex flex-col gap-2 pb-2 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Agents — Most In Progress
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate(sd('admin/agents-pending'))} className="h-8 w-full gap-1 text-xs sm:w-auto">
                View All <ArrowRight className="h-3 w-3" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {dashLoading ? (
                <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
              ) : agentsDisplayed.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">No agent data</p>
              ) : agentsDisplayed.map((agent, i) => {
                const name = fmtCell(agent.employee_name ?? agent.name ?? agent.full_name);
                const { inProgress, assigned } = agentTicketCounts(agent);
                const agentId = String(agent.emp_id ?? agent.id ?? '').trim();
                const openInProgressTickets = () => {
                  if (!agentId) return;
                  navigate(buildAdminAgentInProgressTicketsPath(agentId));
                };
                return (
                  <div
                    key={String(agent.id ?? i)}
                    className="rounded-lg border p-2.5 transition-colors hover:bg-muted/50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="break-words text-sm font-medium">{name}</p>
                        <p className="break-words text-xs text-muted-foreground">
                          {assigned} assigned
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={openInProgressTickets}
                        disabled={!agentId}
                        title={`View ${name}'s in progress tickets`}
                        className={cn(
                          "ml-2 flex flex-shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize transition-colors",
                          statusColors.open,
                          agentId
                            ? "cursor-pointer border-transparent hover:border-current hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                            : "cursor-default border-transparent opacity-70"
                        )}
                      >
                        <span>{inProgress} in progress</span>
                        {agentId ? <ArrowUpRight className="h-3 w-3" /> : null}
                      </button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
